import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  DownloadIcon,
  InboxIcon,
  LinkIcon,
  ShieldAlertIcon,
  UserXIcon,
} from 'lucide-react';
import type {
  IncidentSignalWeergave,
  IncidentStatus,
  SecurityIncidentWeergave as SecurityIncident,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiGet } from '../api/client.js';
import { LogKoppelPaneel } from '../components/beheer-incidenten/LogKoppelPaneel.js';
import { SignaalKaarten } from '../components/beheer-incidenten/SignaalKaarten.js';
import { TermijnBadge } from '../components/beheer-incidenten/TermijnBadge.js';
import { TriageFormulier } from '../components/beheer-incidenten/TriageFormulier.js';
import {
  ERNST_LABEL,
  GEGEVENSSOORT_LABEL,
  GROEP_LABEL,
  STATUS_CHIP,
  STATUS_LABEL,
  VRAAG_TITEL,
} from '../components/beheer-incidenten/labels.js';
import {
  formatDatum,
  formatMoment,
  isAnoniemGemeld,
  termijnStatus,
} from '../components/beheer-incidenten/termijn.js';

/**
 * Beheer -> Incidenten — het werkscherm van de privacy-contactpersoon.
 *
 * De klok is de reden dat dit scherm bestaat: elke melding toont wat er nog
 * aan tijd over is, en op welke termijn dat slaat. De 72-uurstermijn richting
 * de Autoriteit Persoonsgegevens verschijnt alleen bij meldingen die bij
 * triage als datalek zijn gekwalificeerd (plan §3); de rest toont hoelang hij
 * al op die triage wacht.
 *
 * RBAC: suppohrt_admin, afgedwongen aan de route (RoleGuard) én server-side.
 * Gemodelleerd naar BeheerWijzigingsverzoeken: wachtrij, filters, detail,
 * actie.
 */

type StatusFilter = IncidentStatus | '';

/**
 * De countdown moet lopen zonder dat iemand ververst. Eén tik per minuut is
 * genoeg voor een termijn in uren en houdt het scherm stil.
 */
function useNu(): number {
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNu(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return nu;
}

export function BeheerIncidentenPage(): JSX.Element {
  const nu = useNu();
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [signals, setSignals] = useState<IncidentSignalWeergave[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [alleenDatalekken, setAlleenDatalekken] = useState(false);
  // ?id= komt uit de incidentmail: de melding meteen openen, daarna de
  // parameter weghalen zodat "terug" gewoon de wachtrij toont.
  const [searchParams, setSearchParams] = useSearchParams();
  const [geselecteerdId, setGeselecteerdId] = useState<string | null>(() => searchParams.get('id'));
  useEffect(() => {
    if (searchParams.has('id')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      // De signalen zijn een aparte collectie; een lege of falende
      // signalenlijst mag de wachtrij niet tegenhouden.
      const [lijst, signalen] = await Promise.all([
        apiGet<{ incidents: SecurityIncident[] }>('/api/v1/beheer/incidents'),
        apiGet<{ signals: IncidentSignalWeergave[] }>('/api/v1/beheer/incident-signals').catch(
          () => ({
            signals: [] as IncidentSignalWeergave[],
          }),
        ),
      ]);
      setIncidents(lijst.incidents);
      setSignals(signalen.signals);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? `Laden mislukt (${err.status}): ${err.message}`
          : 'De meldingen konden niet geladen worden.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const zichtbaar = useMemo(() => {
    const gefilterd = incidents.filter((i) => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (alleenDatalekken && i.triage?.isDatalek !== true) return false;
      return true;
    });
    // Nieuwste eerst. Filteren en sorteren gebeurt hier en niet server-side,
    // zodat de klok voor alle meldingen uit dezelfde lijst wordt gerekend.
    return gefilterd.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [incidents, statusFilter, alleenDatalekken]);

  const geselecteerd = incidents.find((i) => i.id === geselecteerdId) ?? null;

  const openstaand = incidents.filter((i) => i.status !== 'afgerond').length;
  const zonderTriage = incidents.filter(
    (i) => i.triage?.isDatalek === undefined || i.triage.isDatalek === null,
  ).length;
  const overschreden = incidents.filter(
    (i) => termijnStatus(i, nu).soort === 'overschreden',
  ).length;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        {geselecteerdId && geselecteerd ? (
          <IncidentDetail
            // Remount bij een andere melding, zodat het triageformulier nooit
            // met de antwoorden van de vorige melding blijft staan.
            key={geselecteerdId}
            incidentId={geselecteerdId}
            samenvatting={geselecteerd}
            nu={nu}
            onTerug={() => setGeselecteerdId(null)}
            onGewijzigd={load}
          />
        ) : (
          <>
            <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
                  <ShieldAlertIcon size={20} className="text-brand-700" />
                  Beheer Incidenten
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-ink-900/65">
                  Meldingen van (mogelijke) informatiebeveiligingsincidenten. Je bepaalt bij triage
                  of een melding een datalek is; pas dan gaat de wettelijke termijn van 72 uur
                  richting de Autoriteit Persoonsgegevens lopen.
                </p>
              </div>
              <RegisterExportKnop />
            </header>

            {!loading && incidents.length > 0 && (
              <p className="mb-4 text-[13px] text-ink-900/60">
                {openstaand} openstaand, {zonderTriage} wacht op triage
                {overschreden > 0 && (
                  <span className="font-semibold text-error">
                    , {overschreden} over de AP-termijn
                  </span>
                )}
                .
              </p>
            )}

            <SignaalKaarten
              signals={signals}
              onReviewed={(nieuwIncidentId) => {
                void load();
                if (nieuwIncidentId) setGeselecteerdId(nieuwIncidentId);
              }}
            />

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900"
              >
                <option value="">Alle statussen</option>
                {(Object.keys(STATUS_LABEL) as IncidentStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-900">
                <input
                  type="checkbox"
                  checked={alleenDatalekken}
                  onChange={(e) => setAlleenDatalekken(e.target.checked)}
                  className="h-4 w-4 accent-brand-600"
                />
                Alleen datalekken
              </label>
            </div>

            {errorMsg && (
              <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-error/10 px-3 py-2 text-sm text-error">
                <AlertCircleIcon size={14} /> {errorMsg}
              </div>
            )}

            {loading ? (
              <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
                Laden…
              </div>
            ) : zichtbaar.length === 0 ? (
              <LegeWachtrij
                gefilterd={incidents.length > 0}
                alleenDatalekken={alleenDatalekken}
                status={statusFilter}
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {zichtbaar.map((i) => (
                  <IncidentKaart
                    key={i.id}
                    incident={i}
                    nu={nu}
                    onOpen={() => setGeselecteerdId(i.id)}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function LegeWachtrij({
  gefilterd,
  alleenDatalekken,
  status,
}: {
  gefilterd: boolean;
  alleenDatalekken: boolean;
  status: StatusFilter;
}): JSX.Element {
  const uitleg = !gefilterd
    ? 'Er zijn nog geen meldingen binnengekomen. Zodra iemand een incident meldt via "Incident melden", verschijnt de melding hier met de termijn erbij.'
    : alleenDatalekken
      ? 'Geen enkele melding in deze weergave is bij triage als datalek gekwalificeerd. Haal het vinkje weg om alle meldingen te zien.'
      : status
        ? `Geen meldingen met de status "${STATUS_LABEL[status]}". Kies "Alle statussen" om de rest te zien.`
        : 'Geen meldingen in deze weergave.';

  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-ring2 bg-white px-6 py-12 text-center">
      <InboxIcon size={28} className="text-ink-900/30" />
      <p className="text-sm font-semibold text-ink-900">
        {gefilterd ? 'Niets gevonden in deze weergave' : 'Geen openstaande meldingen'}
      </p>
      <p className="max-w-md text-sm text-ink-900/60">{uitleg}</p>
    </div>
  );
}

function IncidentKaart({
  incident,
  nu,
  onOpen,
}: {
  incident: SecurityIncident;
  nu: number;
  onOpen: () => void;
}): JSX.Element {
  const anoniem = isAnoniemGemeld(incident.melderOpgave.melderNaam);
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full rounded-2xl border border-ring2 bg-white p-4 text-left shadow-card transition-colors hover:bg-brand-50/50"
      >
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
          <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_CHIP[incident.status]}`}>
            {STATUS_LABEL[incident.status]}
          </span>
          {incident.triage?.isDatalek === true && (
            <span className="rounded-full bg-error/10 px-2 py-0.5 font-semibold text-error">
              Datalek
            </span>
          )}
          {incident.bron === 'log_signaal' && (
            <span className="rounded-full bg-ring2-bg px-2 py-0.5 font-medium text-ring2-fg">
              Uit log-signaal
            </span>
          )}
          {anoniem && (
            <span className="inline-flex items-center gap-1 rounded-full bg-ink-50 px-2 py-0.5 font-medium text-ink-900/60">
              <UserXIcon size={11} /> Anoniem gemeld
            </span>
          )}
          {incident.logKoppelingen.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-ink-50 px-2 py-0.5 font-medium text-ink-900/60">
              <LinkIcon size={11} /> {incident.logKoppelingen.length} logregels
            </span>
          )}
          <span className="text-ink-900/45">Binnengekomen {formatMoment(incident.createdAt)}</span>
        </div>

        <TermijnBadge incident={incident} nu={nu} compact />

        {/* De titel van de behandelaar wint van de omschrijving van de melder:
            die laatste is vrije tekst, kan lang zijn en kan PII bevatten. */}
        {incident.triage?.titel?.trim() ? (
          <p className="mt-2 text-sm font-semibold text-ink-900">{incident.triage.titel}</p>
        ) : (
          <p className="mt-2 line-clamp-2 text-sm text-ink-900/80">
            {incident.melderOpgave.omschrijving?.trim() ||
              incident.melderOpgave.ontdekkingsWijze?.trim() ||
              'Geen omschrijving ingevuld door de melder.'}
          </p>
        )}
        <p className="mt-1 text-[12px] text-ink-900/50">
          Incident op {formatDatum(incident.melderOpgave.datumLek)}, ontdekt op{' '}
          {formatDatum(incident.melderOpgave.datumOntdekt)}
          {incident.aangemaaktDoor
            ? `, aangemaakt door ${incident.aangemaaktDoor} uit een logsignaal`
            : !anoniem && `, gemeld door ${incident.melderOpgave.melderNaam}`}
        </p>
      </button>
    </li>
  );
}

function IncidentDetail({
  incidentId,
  samenvatting,
  nu,
  onTerug,
  onGewijzigd,
}: {
  incidentId: string;
  samenvatting: SecurityIncident;
  nu: number;
  onTerug: () => void;
  onGewijzigd: () => void;
}): JSX.Element {
  // De wachtrij levert al een volledig document; het detail-endpoint is de
  // gezaghebbende bron zodra iemand parallel triage doet. Tot dat antwoord er
  // is tonen we de versie uit de wachtrij, zodat het scherm niet knippert.
  const [detail, setDetail] = useState<SecurityIncident>(samenvatting);
  const [err, setErr] = useState<string | null>(null);

  const herlaad = useCallback(async () => {
    try {
      const verse = await apiGet<SecurityIncident>(`/api/v1/beheer/incidents/${incidentId}`);
      setDetail(verse);
      setErr(null);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? `Melding kon niet geladen worden (${e.status}): ${e.message}`
          : 'Melding kon niet geladen worden.',
      );
    }
  }, [incidentId]);

  useEffect(() => {
    void herlaad();
  }, [herlaad]);

  const opgave = detail.melderOpgave;
  const anoniem = isAnoniemGemeld(opgave.melderNaam);

  return (
    <div>
      <button
        type="button"
        onClick={onTerug}
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-petrol-500 hover:text-petrol-700"
      >
        <ArrowLeftIcon size={15} /> Terug naar de wachtrij
      </button>

      <header className="mb-4">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
          <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_CHIP[detail.status]}`}>
            {STATUS_LABEL[detail.status]}
          </span>
          {detail.bron === 'log_signaal' && (
            <span className="rounded-full bg-ring2-bg px-2 py-0.5 font-medium text-ring2-fg">
              Aangemaakt uit een log-signaal
            </span>
          )}
          {/* Het meldingsnummer is wat de melder in zijn bevestiging zag;
              de doc-id is de terugvaloptie voor oudere meldingen. */}
          <span className="text-ink-900/45">
            Meldingsnummer {detail.meldingsnummer ?? detail.id}
          </span>
        </div>
        <h1 className="text-xl font-bold text-ink-900">
          Melding van {formatDatum(opgave.datumLek)}
        </h1>
        <div className="mt-2">
          <TermijnBadge incident={detail} nu={nu} />
        </div>
      </header>

      {err && (
        <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-error/10 px-3 py-2 text-sm text-error">
          <AlertCircleIcon size={14} /> {err}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <MelderOpgaveBlok incident={detail} anoniem={anoniem} />
        <Tijdlijn incident={detail} />
        <TriageFormulier
          incident={detail}
          onSaved={() => {
            void herlaad();
            onGewijzigd();
          }}
        />
        <LogKoppelPaneel
          incident={detail}
          onLinked={() => {
            void herlaad();
            onGewijzigd();
          }}
        />
      </div>
    </div>
  );
}

function MelderOpgaveBlok({
  incident,
  anoniem,
}: {
  incident: SecurityIncident;
  anoniem: boolean;
}): JSX.Element {
  const o = incident.melderOpgave;
  return (
    <section className="rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <h2 className="text-base font-bold text-ink-900">Opgave van de melder</h2>
      <p className="mt-1 text-sm text-ink-900/65">
        De elf antwoorden zoals de melder ze gaf. Dit blok verandert nooit, ook niet als je bij
        triage tot een ander oordeel komt: bij een melding aan de AP moet te reconstrueren zijn wat
        de melder wist op het moment van melden.
      </p>

      {anoniem && (
        <p className="mt-3 inline-flex items-start gap-1.5 rounded-md bg-ink-50 px-3 py-2 text-[12px] text-ink-900/70">
          <UserXIcon size={14} className="mt-0.5 shrink-0" />
          Deze melding is anoniem gedaan. Er worden hier geen accountgegevens van de melder getoond.
        </p>
      )}

      <dl className="mt-4 flex flex-col gap-4">
        <Antwoord nummer={1}>
          {anoniem ? (
            <span className="text-ink-900/60">Anoniem gemeld</span>
          ) : (
            <>
              {o.melderNaam}
              {o.melderContactOptIn ? (
                <span className="ml-2 rounded-sm bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700">
                  Mag benaderd worden via de accountgegevens
                </span>
              ) : (
                <span className="ml-2 rounded-sm bg-ink-50 px-2 py-0.5 text-[11px] font-medium text-ink-900/60">
                  Geen toestemming voor contact via het account
                </span>
              )}
            </>
          )}
        </Antwoord>
        <Antwoord nummer={2}>{o.ontdekkingsWijze?.trim() || <Leeg />}</Antwoord>
        <Antwoord nummer={3}>{o.omschrijving?.trim() || <Leeg />}</Antwoord>
        <Antwoord nummer={4}>{formatDatum(o.datumLek)}</Antwoord>
        <Antwoord nummer={5}>{formatDatum(o.datumOntdekt)}</Antwoord>
        <Antwoord nummer={6}>
          {o.groepen.map((g) => GROEP_LABEL[g]).join(', ')}
          {o.groepenAnders?.trim() ? ` (anders: ${o.groepenAnders.trim()})` : ''}
        </Antwoord>
        <Antwoord nummer={7}>
          {o.gegevenssoorten.map((g) => GEGEVENSSOORT_LABEL[g]).join(', ')}
        </Antwoord>
        <Antwoord nummer={8}>{o.andereOrganisaties ? 'Ja' : 'Nee'}</Antwoord>
        {o.andereOrganisaties && (
          <Antwoord nummer={9}>{o.organisatiesWelke?.trim() || <Leeg />}</Antwoord>
        )}
        <Antwoord nummer={10}>{o.ondernomenActie}</Antwoord>
        <Antwoord nummer={11}>{o.voorgesteldeMaatregelen?.trim() || <Leeg />}</Antwoord>
      </dl>

      {!anoniem && (
        <p className="mt-4 border-t border-ring2/70 pt-3 text-[12px] text-ink-900/50">
          Ingediend {incident.aangemaaktDoor ? `door ${incident.aangemaaktDoor} ` : ''}vanuit
          account {incident.submittedByUserId} (tenant {incident.tenantId}).
        </p>
      )}
    </section>
  );
}

function Antwoord({ nummer, children }: { nummer: number; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-900/50">
        {nummer}. {VRAAG_TITEL[nummer]}
      </dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-ink-900/85">{children}</dd>
    </div>
  );
}

function Leeg(): JSX.Element {
  return <span className="italic text-ink-900/40">Niet ingevuld</span>;
}

interface TijdlijnPunt {
  at: string;
  titel: string;
  detail?: string;
  soort: 'melding' | 'triage' | 'log';
}

function Tijdlijn({ incident }: { incident: SecurityIncident }): JSX.Element {
  const punten: TijdlijnPunt[] = [
    { at: incident.createdAt, titel: 'Melding ontvangen', soort: 'melding' },
  ];
  if (incident.getrieerdOp) {
    const t = incident.triage;
    punten.push({
      at: incident.getrieerdOp,
      titel: 'Triage uitgevoerd',
      detail: [
        t?.isDatalek === true ? 'Gekwalificeerd als datalek' : undefined,
        t?.isDatalek === false ? 'Geen datalek' : undefined,
        t?.ernst ? `Ernst: ${ERNST_LABEL[t.ernst]}` : undefined,
      ]
        .filter(Boolean)
        .join(', '),
      soort: 'triage',
    });
  }
  if (incident.triage?.apGemeldOp) {
    punten.push({
      at: incident.triage.apGemeldOp,
      titel: 'Gemeld aan de Autoriteit Persoonsgegevens',
      soort: 'triage',
    });
  }
  if (incident.afgerondOp) {
    punten.push({ at: incident.afgerondOp, titel: 'Melding afgerond', soort: 'triage' });
  }
  for (const k of incident.logKoppelingen) {
    punten.push({
      at: k.at,
      titel: k.action,
      detail: [k.toelichting, `gekoppeld op ${formatMoment(k.gekoppeldOp)}`]
        .filter(Boolean)
        .join(', '),
      soort: 'log',
    });
  }
  punten.sort((a, b) => a.at.localeCompare(b.at));

  return (
    <section className="rounded-2xl border border-ring2 bg-white p-4 shadow-card">
      <h2 className="text-base font-bold text-ink-900">Tijdlijn</h2>
      <p className="mt-1 text-sm text-ink-900/65">
        De behandeling van deze melding, plus de audit-logregels die eraan gekoppeld zijn. Dit is de
        feitenbasis bij een melding aan de AP.
      </p>
      {incident.logKoppelingen.length === 0 && (
        <p className="mt-3 rounded-md bg-ink-50 px-3 py-2 text-[12px] text-ink-900/60">
          Er zijn nog geen logregels gekoppeld. Gebruik het blok hieronder als het incident sporen
          in de Kennishub achterliet.
        </p>
      )}
      <ol className="mt-4 flex flex-col gap-3 border-l-2 border-brand-100 pl-4">
        {punten.map((p, i) => (
          <li key={`${p.at}-${i}`} className="relative">
            <span
              className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${
                p.soort === 'log' ? 'bg-petrol-500' : 'bg-brand-500'
              }`}
            />
            <p className="text-[12px] text-ink-900/50 tabular-nums">{formatMoment(p.at)}</p>
            <p className="text-sm font-medium text-ink-900">
              {p.soort === 'log' ? (
                <span className="rounded-sm bg-brand-50 px-2 py-0.5 text-[12px] font-medium text-brand-700">
                  {p.titel}
                </span>
              ) : (
                p.titel
              )}
            </p>
            {p.detail && <p className="text-[12px] text-ink-900/60">{p.detail}</p>}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Registerexport. `apiGet` parseert JSON, dus deze download doet zijn eigen
 * fetch. De dev-token wordt hier opnieuw gelezen omdat een lokale sessie
 * anders een 401 op de download krijgt terwijl de rest van het scherm werkt;
 * in productie draagt de cookie de sessie.
 */
const DEV_TOKEN_STORAGE_KEY = 'kennisbank.devToken';

function RegisterExportKnop(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function download(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const token = window.sessionStorage.getItem(DEV_TOKEN_STORAGE_KEY);
      const res = await fetch('/api/v1/beheer/incidents/export', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setErr(`Export mislukt (${res.status}).`);
        return;
      }
      const blob = await res.blob();
      // De backend zet de bestandsnaam in Content-Disposition; die is
      // leidend zodat het register overal dezelfde naam draagt.
      const bestandsnaam =
        /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
        `incidentregister-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = bestandsnaam;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setErr('Export mislukt.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void download()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-ring2 bg-white px-4 py-2 text-sm font-semibold text-ink-900 hover:bg-brand-50 disabled:opacity-50"
      >
        <DownloadIcon size={15} /> {busy ? 'Bezig…' : 'Register exporteren'}
      </button>
      {err && <span className="text-[12px] text-error">{err}</span>}
    </div>
  );
}
